import {
  parseInstrumentSymbol,
  getTickerBaseCurrency as parseTickerBase,
} from '@/features/order/order-form/lib/parse-instrument-symbol'
import { isInverseInstrument } from '@/lib/instrument-util'
import type { OrderFormDenomPreference } from '@/state/order'
import type { SelectedInstrument } from '@/types/dashboard'
import { Denomination } from '@/types/generated/common/trading'
import { Instrument } from '@/types/generated/common/types'

// InstrumentSource accepts either an array (first lookup) or a Map (repeated lookups
// in tables/lists to avoid repeated .find() calls)
export type InstrumentSource = Instrument[] | Map<string, Instrument>

/** A full close retains the position's native size unit regardless of display preference. */
export function defaultInstrumentAmountUnit(
  inst: SelectedInstrument,
  preference: OrderFormDenomPreference = 'auto'
): string {
  if (inst.lockedAmount && inst.lockedAmountUnit) return inst.lockedAmountUnit
  const quoteCurrency = inst.quoteCurrency || resolveQuoteCurrency([], inst.symbol)
  const baseCurrency = inst.baseCurrency || resolveBaseCurrency([], inst.symbol)
  if (preference === 'quote') return quoteCurrency
  if (preference === 'base') return baseCurrency
  return isInverseInstrument(inst.symbol, inst) ? quoteCurrency : baseCurrency
}

function lookup(source: InstrumentSource, symbol: string): Instrument | null {
  if (source instanceof Map) return source.get(symbol) ?? null
  return source.find((i) => i.symbol === symbol) ?? null
}

/** Instrument lookup by symbol. Returns null if not found. */
export function resolveInstrumentBySymbol(
  source: InstrumentSource,
  symbol: string
): Instrument | null {
  return lookup(source, symbol)
}

/**
 * baseCurrency: instrument field first, symbol parse fallback.
 * Pass `hint` if you already have the Instrument object to skip the lookup.
 */
export function resolveBaseCurrency(
  source: InstrumentSource,
  symbol: string,
  hint?: Instrument | null
): string {
  const inst = hint ?? lookup(source, symbol)
  if (inst?.baseCurrency) return inst.baseCurrency.toUpperCase()
  return parseInstrumentSymbol(symbol).baseTicker.toUpperCase()
}

/**
 * quoteCurrency: instrument field first, symbol parse fallback.
 * Pass `hint` if you already have the Instrument object to skip the lookup.
 */
export function resolveQuoteCurrency(
  source: InstrumentSource,
  symbol: string,
  hint?: Instrument | null
): string {
  const inst = hint ?? lookup(source, symbol)
  if (inst?.quoteCurrency) return inst.quoteCurrency.toUpperCase()
  return parseInstrumentSymbol(symbol).quoteCurrency.toUpperCase()
}

/**
 * Denomination rule: options are always base-denominated; everything else
 * compares the leg's amountUnit against its resolved base currency. Pass
 * `hint` if you already have the Instrument object to skip the lookup.
 */
export function resolveDenomination(
  source: InstrumentSource,
  symbol: string,
  itemType: 'instrument' | 'option',
  amountUnit: string | null | undefined,
  hint?: Instrument | null
): Denomination {
  const baseCurrency = resolveBaseCurrency(source, symbol, hint)
  const isBaseAmount = itemType === 'option' || (amountUnit ?? '') === baseCurrency
  return isBaseAmount ? Denomination.DENOMINATION_BASE : Denomination.DENOMINATION_QUOTE
}

/**
 * Effective order quantity in raw units: multiplies by the instrument's
 * contractSize for options where the UI input is in contracts (e.g. TRX: 1
 * contract = 10,000 TRX). Non-option instruments pass amount through as-is.
 */
export function resolveEffectiveAmount(
  source: InstrumentSource,
  symbol: string,
  itemType: 'instrument' | 'option',
  amount: number
): number {
  if (itemType !== 'option') return amount
  const inst = lookup(source, symbol)
  const contractSize = Math.max(1, Number(inst?.contractSize) || 1)
  return amount * contractSize
}

/**
 * Derives baseCurrency from a ticker prefix like "BTC_USDC" or "BTC".
 * - Pair form (contains _): matches instruments by baseCurrency + quoteCurrency
 * - Single base form: falls back to string split (no find() needed)
 */
export function resolveBaseCurrencyFromTicker(source: InstrumentSource, ticker: string): string {
  const upper = ticker.toUpperCase()
  if (upper.includes('_')) {
    const arr = source instanceof Map ? [...source.values()] : source
    const inst = arr.find(
      (i) => `${i.baseCurrency?.toUpperCase()}_${i.quoteCurrency?.toUpperCase()}` === upper
    )
    if (inst?.baseCurrency) return inst.baseCurrency.toUpperCase()
  }
  return parseTickerBase(ticker)
}

/**
 * Reverse-lookup for call sites that only have a bare currency code (no
 * Instrument in hand) — e.g. an account-balance row. Scans `source` for the
 * first instrument whose baseCurrency matches (case-insensitive) AND that
 * actually has an icon_tag set, so one untagged instrument (e.g. a curated
 * override or an UNFILLED one, both of which always carry icon_tag=None)
 * doesn't shadow a tagged sibling for the same currency. Returns undefined
 * if no match has a tag — CryptoIcon falls back to the letter avatar.
 */
export function resolveIconTag(source: InstrumentSource, currency: string): string | undefined {
  const upper = currency.toUpperCase()
  const arr = source instanceof Map ? [...source.values()] : source
  const inst = arr.find((i) => i.baseCurrency?.toUpperCase() === upper && i.enrichment?.iconTag)
  return inst?.enrichment?.iconTag
}
